import { useContext, useEffect, useState } from 'react';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import { CustomerOrder } from '../../../clients/customer-api/order/customer-order';
import { Location } from '../../../clients/quipli-service/location/location';
import { OrderAdditionalFee, OrderProductAdditionalFee, OrderType } from '../../../clients/quipli-service/order/order';
import { convertToMoney } from '../../../util/util';
import { Spinner } from '../../../components/spinner/spinner';
import { Cart } from '../../../clients/customer-api/cart/cart';
import {
  CouponLineItem,
  DamageWaiverLineItem,
  DeliveryLineItem,
  DiscountLineItem,
  FeeLineItem,
  FinancialSummary,
  LineItem,
  PickupLineItem,
  ProductFeeLineItem,
  ProductLineItem,
  SecurityDepositLineItem,
  TaxLineItem,
} from '../../../clients/quipli-service/financials/financials';
import { round } from 'lodash';
import { CheckoutSubmitRequest, ProcessCheckout } from '../checkout-view';
import { useCustomer } from '../../../hooks/use-customer-context';
import { ecommerceToast } from '../../../util/toasts';
import { TenantContext } from '../../../context/tenant-context';
import { useCustomerYorentClient } from '../../../clients/customer-api/customer-yorent-api-client';
import { ProductType } from '../../../clients/quipli-service/product/product';

export const QuoteView = ({ orderId }: { orderId: number }) => {
  const { getOrderById, getLocationById, acceptQuote } = useCustomerAPI();
  const { refreshCartProductCount } = useCustomerYorentClient();
  const { contextPath } = useContext(TenantContext);
  const customer = useCustomer();

  const [order, setOrder] = useState<CustomerOrder>();
  const [location, setLocation] = useState<Location>();
  const [cart, setCart] = useState<Cart>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [summary, setSummary] = useState<FinancialSummary>();

  const onSubmit = async (req: CheckoutSubmitRequest) => {
    if (isSubmitting || !location || !summary || !cart || !customer) return;

    setIsSubmitting(true);
    try {
      await acceptQuote(
        orderId,
        location.id,
        req.paymentMethod,
        req.billingAddressId,
        req.deliveryAddressId,
        req.digitalSignatureDate,
      );
      await refreshCartProductCount();
      window.location.href = `${contextPath}custom/payment_success`;
    } catch (error: any) {
      ecommerceToast(error.message.msg || error.message || 'Error accepting quote.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateSummary = (_order: CustomerOrder, _location: Location) => {
    const currency = _location.currency as Dinero.Currency;
    // Set initial summary
    const productFeeLineItems: ProductFeeLineItem[] = [];

    const productLineItems: ProductLineItem[] = _order.products.map((product) => {
      const additionalFees = product.additionalFees.map(
        (fee: OrderProductAdditionalFee) =>
          ({
            productLineItemId: `${product.id}`,
            productFeeId: +fee.id,
            productId: +fee.productId,
            description: fee.feeTitle,
            type: 'ProductFee',
            percentage: fee.feeType === 'P' ? fee.feeValue || 0 : 0,
            taxable: fee.taxApplied,
            unitPrice: convertToMoney(+fee.feeAppliedValue, currency),
            amount: convertToMoney(+fee.feeAppliedValue, currency),
            recurring: fee.recurring,
            hasCustomAmount: !!fee.updatedAt,
            optional: false, // not needed
            isSelected: true, // not needed
          } as ProductFeeLineItem),
      );

      productFeeLineItems.push(...additionalFees);

      return {
        id: `${product.id}`,
        productId: +product.product.id,
        type: 'Product',
        description: product.product.name,
        transactionType: product.type,
        unitPrice: convertToMoney(+product.customerBuyingPrice || 0, currency),
        quantity: +product.quantity,
        taxable: !product.isTaxFree,
        tax: convertToMoney(+product.tax, currency),
        amount: convertToMoney(
          +product.customerBuyingPrice *
            +product.quantity *
            (product.discountPercentage ? +(1 - product.discountPercentage / 100) : 1),
          currency,
        ),
        hasCustomAmount: false,
      } as ProductLineItem;
    });

    const feeLineItems: FeeLineItem[] = _order.additionalFees.map(
      (fee: OrderAdditionalFee) =>
        ({
          id: fee.id,
          feeId: +fee.feeId,
          description: fee.feeTitle,
          type: 'Fee',
          percentage: fee.feeType === 'P' ? +fee.feeValue || 0 : 0,
          taxable: fee.taxApplied,
          optional: false, // not needed
          isSelected: true,
          unitPrice: convertToMoney(+fee.feeAppliedValue, currency),
          amount: convertToMoney(+fee.feeAppliedValue, currency),
          hasCustomAmount: !!fee.updatedAt,
          quantity: 1,
          recurring: fee.recurring,
        } as FeeLineItem),
    );

    const deliveryFee: DeliveryLineItem = {
      type: 'Delivery',
      hasCustomAmount: false, // not needed
      description: 'Delivery',
      quantity: 1,
      taxable: false, // not needed
      unitPrice: convertToMoney(_order.shippingCharged || 0, currency),
      amount: convertToMoney(_order.shippingCharged || 0, currency),
      recurring: false, // not needed
    };

    const pickupFee: PickupLineItem = {
      type: 'Pickup',
      hasCustomAmount: false, // not needed
      description: 'Pickup',
      quantity: 1,
      taxable: false, // not needed
      unitPrice: convertToMoney(_order.pickupCharged || 0, currency),
      amount: convertToMoney(_order.pickupCharged || 0, currency),
      recurring: false, // not needed
    };

    const lineItems: LineItem[] = [
      ...productLineItems,
      ...productFeeLineItems,
      ...feeLineItems,
      deliveryFee,
      pickupFee,
    ];

    if (_order.damageWaiver && _order.damageWaiver > 0) {
      lineItems.push({
        type: 'DamageWaiver',
        hasCustomAmount: false, // not needed
        description: 'Damage Waiver',
        quantity: 1,
        taxable: false, // not needed
        unitPrice: convertToMoney(_order.damageWaiver || 0, currency),
        amount: convertToMoney(_order.damageWaiver || 0, currency),
        recurring: false, // not needed
        optional: false,
        isSelected: true,
      } as DamageWaiverLineItem);
    }

    if (_order.additionalDiscount && _order.additionalDiscount > 0) {
      lineItems.push({
        type: 'AdditionalDiscount',
        description: 'Additional Discount',
        quantity: 1,
        taxable: false, // not needed
        unitPrice: convertToMoney(-(_order.additionalDiscount || 0), currency),
        amount: convertToMoney(-(_order.additionalDiscount || 0), currency),
        recurring: false, // not needed
      } as DiscountLineItem);
    }

    if (_order.taxCharged && _order.taxCharged > 0) {
      lineItems.push({
        type: 'Tax',
        hasCustomAmount: false, // not needed
        hasCustomPercentage: false, // not needed
        description: 'Tax',
        quantity: 1,
        taxable: false, // not needed
        userIsExempted: false, // not needed
        percentage: round((_order.taxPercentage || 0) * 100, 2),
        unitPrice: convertToMoney(+(_order.taxCharged || 0), currency),
        amount: convertToMoney(+(_order.taxCharged || 0), currency),
        recurring: false, // not needed
        taxBreakdown: _order.taxBreakdown,
      } as TaxLineItem);
    }

    if (_order.discountTotal && _order.discountTotal > 0) {
      lineItems.push({
        type: 'Coupon',
        description: 'Coupon',
        quantity: 1,
        taxable: false, // not needed
        unitPrice: convertToMoney(-(_order.discountTotal || 0), currency),
        amount: convertToMoney(-(_order.discountTotal || 0), currency),
        code: _order.discountCoupon || '-NA-',
        productIds: [],
        recurring: false, // not needed
        isVip: false, // not needed
      } as CouponLineItem);
    }

    if (_order.rentalSecurity && _order.rentalSecurity > 0) {
      lineItems.push({
        type: 'SecurityDeposit',
        description: '',
        quantity: 1,
        taxable: false, // not needed
        unitPrice: convertToMoney(_order.rentalSecurity || 0, currency),
        amount: convertToMoney(_order.rentalSecurity || 0, currency),
        percentage: _order.rentalSecurityPercentage,
        recurring: false, // not needed
      } as SecurityDepositLineItem);
    }

    setSummary({
      total: convertToMoney(_order.netCharged, currency),
      lineItems,
    });
  };

  useEffect(() => {
    const init = async () => {
      const order = await getOrderById(orderId);
      const location = await getLocationById(order.locationId);

      setOrder(order);
      setLocation(location);

      setCart({
        id: '',
        sessionId: '',
        items: order.products.map((p) => ({
          id: `${p.id}`,
          cartId: '',
          type: p.type,
          product: {
            ...p.product,
            ...(p.product.type === ProductType.Custom && {
              name: `${p.name}${p.description ? `: ${p.description}` : ''}`,
            }),
          },
          quantity: p.quantity,
          options: {},
          ...(p.type === OrderType.Rental && {
            startDate: p.rentalStartDate,
            endDate: p.rentalEndDate,
          }),
        })),
        meta: {
          selectedAddresses: {
            billing: order.billingAddress?.id,
            shipping: order.shippingAddress?.id,
          },
        },
      });

      generateSummary(order, location);
    };

    init();
  }, [orderId]);

  return !order || !location || !cart ? (
    <Spinner />
  ) : (
    <ProcessCheckout
      isQuote
      cart={cart}
      fetchCart={async () => {}}
      location={location}
      summary={summary}
      isSubmitting={isSubmitting}
      onSubmit={onSubmit}
    />
  );
};
